import { Injectable } from "@angular/core";

// BFX
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import {
  CcyPairPromiseLookupService,
  CcyPairLookupService,
  RateCache,
  Rate,
  TraderGroupPromiseLookupService,
  RateSubscription,
  CURVE_FWD,
  CURVE_NDF,
  RateSubscriptionPromiseService,
} from "shared/cubiq-services";
import { CURVE_SPT } from "shared/cubiq-services";

// models

// services

export interface EntryToleranceErrorsInfo {
  isWrongSideOfMarket: boolean;
  isOutsideCcyPairEntryTolerance: boolean;
  isOutsideEntryAmtTolerance: boolean;
}
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OEDataModel } from "../../data/models/oe-data.model";
import { DealtPricingService } from "shared/cubiq-services/lookup/pricing/impl/DealtPricingService";
import { OETimeMachineService } from "app/order-entry/services/basic/oe-time-machine.service";

@Injectable()
export class OEToleranceService {
  private ccyPairLookup: CcyPairLookupService;
  private errorCatagoryMap: WeakMap<Array<any>, EntryToleranceErrorsInfo> = new WeakMap();

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    _currencyPairPromiseService: CcyPairPromiseLookupService,
    private rateCache: RateCache,
    _traderGroupPromiseService: TraderGroupPromiseLookupService,
    private rateSubscriptionService: RateSubscriptionPromiseService,
    private pricingService: DealtPricingService,
    private timeMachine: OETimeMachineService,
  ) {
    Promise.all([
      _currencyPairPromiseService.isPrimed(),
      _traderGroupPromiseService.isPrimed(),
    ]).then(
      (values: boolean[]) => {
        if (values[0] !== true) {
          logger.out.error(
            `${this.constructor.name}: _currencyPairPromiseService.isPrimed ${values[0]}`,
          );
        }

        if (values[1] !== true) {
          logger.out.error(
            `${this.constructor.name}: _traderGroupPromiseService.isPrimed ${values[1]}`,
          );
        }

        let ccyPairLookup = _currencyPairPromiseService.getBase();
        let traderGroupLookup = _traderGroupPromiseService.getBase();

        if (ccyPairLookup == null) {
          logger.out.error(
            `${this.constructor.name}: _currencyPairPromiseService.getBase ${ccyPairLookup}`,
          );
        }

        this.ccyPairLookup = ccyPairLookup;

        if (traderGroupLookup == null) {
          logger.out.error(
            `${this.constructor.name}: _traderGroupPromiseService.getBase ${ccyPairLookup}`,
          );
        }
      },
      (err) => {
        logger.out.info(`${this.constructor.name}: failed to prime %O`, err);
      },
    );
  }

  /**********************************************************************/
  /* INITIALIZATION */

  /**********************************************************************/

  /**********************************************************************/
  /* TOLERANCE */

  /**********************************************************************/
  public checkEntryToleranceErrors(
    model: OEDataModel,
    skipSubscriptions: boolean = false,
  ): Promise<Array<any>> {
    return new Promise<Array<any>>((resolve, _reject) => {
      if (model.toleranceAccept) {
        resolve([]);
        return;
      }

      let order = model.requestModel.rootOrder;
      let ccyPair = this.ccyPairLookup.getCcyPair(order.base, order.nonBase);
      let forwardSubscriptions: Array<RateSubscription> = null;

      if (
        order.forwardTrackingType === CubiqTS.RequestTrackingType.OUTRIGHT_RATE &&
        (order.product == CubiqTS.Product.FWD || order.product == CubiqTS.Product.NDF) &&
        this.shouldSubscribeBeforeCheck(ccyPair)
      ) {
        let subscriptions: RateSubscription[] = [];
        let curve: string = order.product === CubiqTS.Product.NDF ? CURVE_NDF : CURVE_FWD;
        let valueDate: Date = null;

        if (order.valueDate instanceof CubiqTS.StandardDateImpl) {
          let dt: CubiqTS.StandardDateImpl = order.valueDate;
          const localDate = new Date(dt.year, dt.month - 1, dt.day);
          const timezoneOffset = localDate.getTimezoneOffset() * 60000;
          valueDate = new Date(localDate.getTime() - timezoneOffset);
        }

        let subscription: RateSubscription = new RateSubscription(
          ccyPair.base + ccyPair.nonBase,
          curve,
          order.tenor,
          valueDate,
        );
        if (!skipSubscriptions) {
          logger.out.info(
            `${this.constructor.name}: for refId:${model.refId} requesting subscription: ${subscription}`,
          );
          subscriptions.push(subscription);
        }

        this.rateSubscriptionService
          .subscribeWithChecks(subscriptions)
          .then((results: RateSubscription[]) => {
            if (
              results != null &&
              results.length == 1 &&
              results[0] instanceof CubiqTS.MarketDataEventImpl
            ) {
              forwardSubscriptions = results;
            }

            let pointsRate: Rate = this.rateCache.getRateByKey(subscription.key);

            resolve(this.doToleranceChecks(model.requestModel, ccyPair, order, model, pointsRate));
          })
          .finally(() => {
            if (forwardSubscriptions != null && forwardSubscriptions.length > 0) {
              this.rateSubscriptionService.unsubscribeWithChecks(forwardSubscriptions);
            }
          });
      } else {
        resolve(this.doToleranceChecks(model.requestModel, ccyPair, order, model));
      }
    });
  }

  public checkEntryAmountToleranceErrors(model: OEDataModel): Array<any> {
    if (model.toleranceAmountAccept) {
      return [];
    }

    let order =
      model.requestModel != null && model.requestModel.rootOrder != null
        ? model.requestModel.rootOrder
        : model.bmarkRequestModel.fixingOrderData;
    let errors = [];

    let isOutsideEntryAmtTolerance: boolean = false;

    try {
      if (this.isOutsideEntryAmtTolerance(order, errors, model.requestModel)) {
        isOutsideEntryAmtTolerance = true;
        logger.out.info(
          `${this.constructor.name}: order isOutsideEntryAmtTolerance refId ${model.refId} pair: ${order.base}${order.nonBase}}`,
        );
      }
    } catch (e) {
      logger.out.error(`Error checking for entry amount tolerance: ${order?.orderId}`, e);
    }

    this.errorCatagoryMap.set(errors, {
      isWrongSideOfMarket: false,
      isOutsideCcyPairEntryTolerance: false,
      isOutsideEntryAmtTolerance: isOutsideEntryAmtTolerance,
    });

    return errors;
  }

  protected getTrackingFwdPts(
    order: CubiqTS.OrderRequestData,
    ccyPair: CubiqTS.CurrencyPairImpl,
    fwdRate: Rate,
  ): CubiqTS.BigNumber {
    let trackingFwdPts: CubiqTS.BigNumber = new CubiqTS.BigNumber(0);
    let invert: boolean = order.base == ccyPair.nonBase && order.nonBase == ccyPair.base;
    let bankSellsBase: boolean =
      (order.base == order.fixedCcy && order.clientIntent == CubiqTS.OrderIntent.SELL) ||
      (order.clientIntent == CubiqTS.OrderIntent.BUY && order.nonBase == order.fixedCcy);

    if (invert) {
      bankSellsBase = !bankSellsBase;
    }

    if (fwdRate != null) {
      if (CubiqTS.RequestOrderType.CALL === order.requestOrderType) {
        trackingFwdPts = new CubiqTS.BigNumber(fwdRate.midRate);
      } else {
        trackingFwdPts = new CubiqTS.BigNumber(bankSellsBase ? fwdRate.bidRate : fwdRate.offerRate);
      }
    }

    return trackingFwdPts;
  }

  protected doToleranceChecks(
    request: CubiqTS.LinkedOrderRequest | CubiqTS.LinkedOrderModificationRequest,
    ccyPair: CubiqTS.CurrencyPairImpl,
    order: CubiqTS.OrderRequestDataImpl,
    model: OEDataModel,
    fwdRate: Rate = null,
  ): Array<any> {
    let errors = [];
    let isWrongSideOfMarket = false;
    let isOutsideCcyPairEntryTolerance = false;

    if (this.isWrongSideOfMarket(ccyPair, order, model.client, errors, fwdRate, request)) {
      isWrongSideOfMarket = true;
      logger.out.info(
        `${this.constructor.name}: order isWrongSideOfMarket refId:${model.refId} pair: ${order.base}${order.nonBase}`,
      );
    }

    if (
      this.isOutsideCcyPairEntryTolerance(order, ccyPair, model.client, errors, fwdRate, request)
    ) {
      isOutsideCcyPairEntryTolerance = true;
      logger.out.info(
        `${this.constructor.name}: order isOutsideCcyPairEntryTolerance refId ${
          model.refId
        } pair: ${order.base}${order.nonBase} ${ccyPair.entryRateTolerance.toString()} ${
          CubiqTS.UnitType[ccyPair.entryRateToleranceUnits]
        }`,
      );
    }

    if (
      !isWrongSideOfMarket &&
      !isOutsideCcyPairEntryTolerance &&
      order.sibling &&
      CubiqTS.OrderRelationshipType.OCO === order.sibling.relationshipType
    ) {
      let ocoOrder = order.sibling.order;
      if (this.isWrongSideOfMarket(ccyPair, ocoOrder, model.client, errors, fwdRate)) {
        isWrongSideOfMarket = true;
        logger.out.info(
          `${this.constructor.name}: ocoOrder isWrongSideOfMarket refId:${model.refId} pair: ${order.base}${order.nonBase}`,
        );
      }

      if (this.isOutsideCcyPairEntryTolerance(ocoOrder, ccyPair, model.client, errors, fwdRate)) {
        isOutsideCcyPairEntryTolerance = true;
        logger.out.info(
          `${this.constructor.name}: ocoOrder isOutsideCcyPairEntryTolerance refId ${
            model.refId
          } pair: ${order.base}${order.nonBase} ${ccyPair.entryRateTolerance.toString()} ${
            CubiqTS.UnitType[ccyPair.entryRateToleranceUnits]
          }`,
        );
      }
    }

    this.errorCatagoryMap.set(errors, {
      isWrongSideOfMarket: isWrongSideOfMarket,
      isOutsideCcyPairEntryTolerance: isOutsideCcyPairEntryTolerance,
      isOutsideEntryAmtTolerance: false,
    });

    return errors;
  }

  public getEntryToleranceErrorsInfo(errors: Array<any>): EntryToleranceErrorsInfo | null {
    let info = this.errorCatagoryMap.get(errors);

    if (!info) {
      return null;
    }

    return info;
  }

  public shouldSubscribeBeforeCheck(ccyPair: CubiqTS.CurrencyPair): boolean {
    return ccyPair.failWrongSideOfMktFg == false || this.hasOutsideCcyPairEntryTolerance(ccyPair);
  }

  protected hasOutsideCcyPairEntryTolerance(ccyPair: CubiqTS.CurrencyPair): boolean {
    return (
      ccyPair &&
      ccyPair.entryRateToleranceUnits != null &&
      ccyPair.entryRateTolerance == Object(ccyPair.entryRateTolerance) &&
      ccyPair.entryRateTolerance.comparedTo(0) > 0
    );
  }

  protected hasOutsideEntryAmtTolerance(ccyPair: CubiqTS.CurrencyPair): boolean {
    return (
      ccyPair && ccyPair.entryAmtTolerance != null && ccyPair.entryAmtTolerance.comparedTo(0) > 0
    );
  }

  protected isOutsideEntryAmtTolerance(
    order: CubiqTS.OrderRequestData | CubiqTS.FixingOrderRequestData,
    errors: any[],
    request: CubiqTS.LinkedOrderRequest | CubiqTS.LinkedOrderModificationRequest = null,
  ): boolean {
    let relevantLegs: Array<CubiqTS.OrderRequestData | CubiqTS.FixingOrderRequestData> = [];
    if (order instanceof CubiqTS.OrderRequestDataImpl && this.supportsEntryAmountCheck(order)) {
      relevantLegs.push(order);
    } else if (order instanceof CubiqTS.FixingOrderRequestDataImpl) {
      relevantLegs.push(order);
    }

    let strategy: boolean = false;
    if (request instanceof CubiqTS.GammaOrderRequestImpl) {
      relevantLegs = request.getOrderedRungs();
      strategy = true;

      if (relevantLegs.length > 0) {
        let maxLeg = relevantLegs[0];
        for (let i: number = 1; i < relevantLegs.length; i++) {
          if (new CubiqTS.BigNumber(maxLeg.fixedAmount).isLessThan(relevantLegs[i].fixedAmount)) {
            maxLeg = relevantLegs[i];
          }
        }
        relevantLegs = [maxLeg];
      }
    } else if (order instanceof CubiqTS.OrderRequestDataImpl) {
      const oldLength = relevantLegs.length;
      this.getRelevantStrategyLegs(order, true, relevantLegs);
      strategy = relevantLegs.length > oldLength;
    }

    let failedLeg: Array<CubiqTS.OrderRequestData | CubiqTS.FixingOrderRequestData> = [];

    relevantLegs.forEach((o) => {
      let base: string = o.base;
      let nonBase: string = o.nonBase;

      let ccyPair: CubiqTS.CurrencyPairImpl = this.ccyPairLookup.getCcyPair(base, nonBase, true);
      if (ccyPair != null && this.hasOutsideEntryAmtTolerance(ccyPair)) {
        let amount = o.fixedAmount;

        if (o.fixedCcy != ccyPair.base) {
          let rate = o instanceof CubiqTS.OrderRequestDataImpl ? o.requestRate : null;
          let invert: boolean = ccyPair.base != o.base;

          if (
            o.requestOrderType === CubiqTS.RequestOrderType.ALGO ||
            o.requestOrderType === CubiqTS.RequestOrderType.BMARK ||
            o.requestOrderType === CubiqTS.RequestOrderType.MARKET
          ) {
            let marketRate = this.rateCache.getRate(ccyPair.base + ccyPair.nonBase, CURVE_SPT);
            rate = marketRate?.midRate;
          }

          if (rate == null) {
            return;
          }

          let orderRate: CubiqTS.BigNumber = new CubiqTS.BigNumber(rate);

          if (invert) {
            amount = orderRate.times(amount).toFixed();
          } else {
            amount = new CubiqTS.BigNumber(amount).div(orderRate).toFixed();
          }
        }

        if (new CubiqTS.BigNumber(amount).isGreaterThan(ccyPair.entryAmtTolerance)) {
          if (strategy) {
            let pair: string = ccyPair.base + ccyPair.nonBase;
            this.addError(errors, "order.entry.tolerance.amount.strategy.error", [
              o.fixedAmount,
              pair,
              ccyPair.entryAmtTolerance.toFixed(),
            ]);
          } else {
            this.addError(errors, "order.entry.tolerance.amount.error", [
              ccyPair.entryAmtTolerance.toFixed(),
            ]);
          }
          failedLeg.push(o);
        }
      }
    });

    return failedLeg.length > 0;
  }

  protected isOutsideCcyPairEntryTolerance(
    order: CubiqTS.OrderRequestData,
    pair: CubiqTS.CurrencyPair,
    client: CubiqTS.ClientImpl,
    errors: any[],
    fwdRate: Rate,
    request: CubiqTS.LinkedOrderRequest | CubiqTS.LinkedOrderModificationRequest = null,
  ): boolean {
    if (!this.hasOutsideCcyPairEntryTolerance(pair)) {
      return false;
    }

    if (
      order.requestOrderType === CubiqTS.RequestOrderType.BMARK ||
      order.requestOrderType === CubiqTS.RequestOrderType.MARKET
    ) {
      return false;
    }

    if (request instanceof CubiqTS.GammaOrderRequestImpl) {
      let relevantGammaLegs: Array<CubiqTS.OrderRequestDataImpl> = this.getRelevantGammaLegs(
        request,
        false,
      );
      let failedLeg: CubiqTS.OrderRequestDataImpl[] = relevantGammaLegs.filter((o) =>
        this.isOutsideCcyPairEntryTolerance(o, pair, client, errors, fwdRate),
      );

      if (failedLeg != null && failedLeg.length > 0) {
        failedLeg.forEach((leg) => {
          logger.out.info(
            `${this.constructor.name}: Leg ${leg.externalClientReference} of strategy ${request.linkReference} failed entry tolerance check.`,
          );
        });
      }

      return failedLeg != null;
    }

    let rateStr = "0";
    let rateVal = new CubiqTS.BigNumber("0");
    // always check spot rate
    let rate = this.rateCache.getRate(pair.base + pair.nonBase, CURVE_SPT);

    let trackingSide: CubiqTS.RateSideType = this.getTrackingSide(order);
    let isInvert: boolean = order.base + order.nonBase != pair.base + pair.nonBase;

    if (isInvert && rate != null) {
      rate = rate.invert();
    }
    rateStr = this.getCurrentRate(order, rate, trackingSide, pair, isInvert);
    if (rateStr != "") {
      rateVal = new CubiqTS.BigNumber(rateStr);
    }

    // logic for markup will need to be added
    let fwdPoints: CubiqTS.BigNumber = this.getTrackingFwdPts(order, pair, fwdRate);
    let trackingVal = this.getTrackingRate(order, pair, client, isInvert, fwdPoints);
    let trackingStr = this.getRoundedRateString(trackingVal, isInvert, pair);

    let diffAmt = rateVal.minus(trackingVal).absoluteValue();
    let maxDiffAmt: CubiqTS.BigNumber;
    let toleranceExceeded: boolean = false;

    if (CubiqTS.UnitType.PERCENT == pair.entryRateToleranceUnits) {
      maxDiffAmt = rateVal.times(pair.entryRateTolerance.dividedBy("100"));
      toleranceExceeded = diffAmt.comparedTo(maxDiffAmt) > 0;

      if (toleranceExceeded) {
        this.addError(errors, "order.entry.tolerance.rate.percent.error", [
          trackingStr,
          pair.entryRateTolerance.toFixed(2),
          rateStr,
        ]);
      }
    } else if (CubiqTS.UnitType.PIPS == pair.entryRateToleranceUnits) {
      let pips: number = isInvert ? pair.spotInvertDecimalsQty : pair.rateDecimalsQty;
      maxDiffAmt = pair.entryRateTolerance.times(new CubiqTS.BigNumber("10").pow(-pips));
      toleranceExceeded = diffAmt.comparedTo(maxDiffAmt) > 0;
      if (toleranceExceeded) {
        this.addError(errors, "order.entry.tolerance.rate.pips.error", [
          trackingStr,
          pair.entryRateTolerance.toFixed(0),
          rateStr,
        ]);
      }
    }

    return toleranceExceeded;
  }

  protected isWrongSideOfMarket(
    pair: CubiqTS.CurrencyPair,
    order: CubiqTS.OrderRequestData,
    client: CubiqTS.ClientImpl,
    errors: Array<any>,
    fwdRate: Rate,
    request: CubiqTS.LinkedOrderRequest | CubiqTS.LinkedOrderModificationRequest = null,
  ): any {
    let rate: Rate;

    if (request instanceof CubiqTS.GammaOrderRequestImpl) {
      let relevantGammaLegs: Array<CubiqTS.OrderRequestDataImpl> = this.getRelevantGammaLegs(
        request,
        true,
      );
      let failedLeg: CubiqTS.OrderRequestDataImpl[] = relevantGammaLegs.filter((o) =>
        this.isWrongSideOfMarket(pair, o, client, errors, fwdRate),
      );

      if (failedLeg != null && failedLeg.length > 0) {
        failedLeg.forEach((leg) => {
          logger.out.info(
            `${this.constructor.name}: Leg ${leg.externalClientReference} of strategy ${request.linkReference} failed wrong side of market check.`,
          );
        });
      }

      return failedLeg != null;
    }

    if (!this.canCheckSideOfMarket(order, pair)) {
      // let server handle
      return false;
    }

    rate = this.rateCache.getRate(pair.base + pair.nonBase, CURVE_SPT, CubiqTS.Tenor.SPOT);

    if (!rate) {
      // no rate let server handle
      return false;
    }

    if (!this.isTpOrStop(order)) {
      // only check TP & stops
      return false;
    }

    let trackingSide: CubiqTS.RateSideType = this.getTrackingSide(order);
    let isInvert: boolean = order.base + order.nonBase != pair.base + pair.nonBase;

    if (isInvert && rate != null) {
      rate = rate.invert();
    }

    let rateStr = this.getCurrentRate(order, rate, trackingSide, pair, isInvert);

    let rateVal = new CubiqTS.BigNumber(rateStr);
    let fwdPoints: CubiqTS.BigNumber = this.getTrackingFwdPts(order, pair, fwdRate);

    // logic for markup will need to be added
    let trackingVal = this.getTrackingRate(order, pair, client, isInvert, fwdPoints);

    if (this.isOrderAboveMarket(order, pair) && trackingVal.comparedTo(rateVal) <= 0) {
      this.addError(errors, "order.entry.tolerance.sideofmarket.error", [
        trackingVal.toString(),
        rateStr,
      ]);

      return true;
    } else if (this.isOrderBelowMarket(order, pair) && trackingVal.comparedTo(rateVal) >= 0) {
      this.addError(errors, "order.entry.tolerance.sideofmarket.error", [
        trackingVal.toString(),
        rateStr,
      ]);

      return true;
    }

    return false;
  }

  /**
   * Should the order have its Side Of Market state checked?This will figure out what
   * side of market flag on the pair to use and return it
   * @param order
   * @param pair
   * @returns
   */
  canCheckSideOfMarket(order: CubiqTS.OrderRequestData, pair: CubiqTS.CurrencyPair) {
    const activation = order.activationDetails;
    let handledOnServer: boolean;
    if (this.isActivationInFuture(activation)) {
      // Any activation specified must be in the future the UI does not permit activation in the past
      handledOnServer = pair.failWrongSideOfMktFutureFg;
    } else {
      handledOnServer = pair.failWrongSideOfMktFg;
    }
    return !handledOnServer;
  }

  /**
   * In exceptional circumstances, the OE may set a Good From that is before now (e.g. if edited right before activation)
   * so need to check actual value - most times, activation will be null when activation is in the past
   * @param activation
   * @returns
   */
  private isActivationInFuture(activation: CubiqTS.ActivationDetailsImpl): boolean {
    let result = false;
    if (activation) {
      const dateStr = activation.activationDate;
      const timeStr = activation.activationTime;
      const tz = activation.timezone;
      const actMoment = this.timeMachine.createMoment4StringArray(dateStr, timeStr, tz);

      if (actMoment != null) {
        const now = CubiqTS.moment();
        result = now.isBefore(actMoment);
      }
    }
    return result;
  }

  private getCurrentRate(
    order: CubiqTS.OrderRequestData,
    rate: Rate,
    trackingSide: CubiqTS.RateSideType,
    pair: CubiqTS.CurrencyPair,
    isInvert: boolean,
  ) {
    if (rate == null) {
      return "";
    }
    if (
      (CubiqTS.RateSideType.BID == trackingSide &&
        CubiqTS.RequestOrderType.SL_ASK != order.requestOrderType &&
        CubiqTS.RequestOrderType.TSL_ASK != order.requestOrderType) ||
      CubiqTS.RequestOrderType.TSL_BID == order.requestOrderType ||
      CubiqTS.RequestOrderType.SL_BID == order.requestOrderType
    ) {
      return this.getFormattedRate(rate.bidRate, pair, isInvert);
    } else {
      return this.getFormattedRate(rate.offerRate, pair, isInvert);
    }
  }

  private getTrackingRate(
    order: CubiqTS.OrderRequestData,
    pair: CubiqTS.CurrencyPair,
    client: CubiqTS.ClientImpl,
    isInvert: boolean,
    fwdPoints: CubiqTS.BigNumber,
  ): CubiqTS.BigNumber {
    let spotTrackingRate: CubiqTS.BigNumber;

    if (order.markup) {
      let markupDecs: CubiqTS.BigNumber = this.markupPipsToDecs(order, pair, client, isInvert);
      let tracking: CubiqTS.BigNumber = new CubiqTS.BigNumber(order.requestRate).plus(markupDecs);
      if (tracking.isLessThan(0)) {
        tracking = new CubiqTS.BigNumber(0);
      }
      spotTrackingRate = tracking;
    } else {
      spotTrackingRate = new CubiqTS.BigNumber(order.requestRate);
    }

    try {
      if (fwdPoints != null && spotTrackingRate.isGreaterThan(0)) {
        if (isInvert) {
          spotTrackingRate = new CubiqTS.BigNumber("1").dividedBy(spotTrackingRate);
        }

        spotTrackingRate = spotTrackingRate.minus(fwdPoints);

        if (isInvert) {
          spotTrackingRate = new CubiqTS.BigNumber("1").dividedBy(spotTrackingRate);
        }

        let roundedRateStr: string = this.getRoundedRateString(spotTrackingRate, isInvert, pair);
        spotTrackingRate = new CubiqTS.BigNumber(roundedRateStr);
      }
    } catch (e) {
      logger.out.error(
        "Failed to calculate spot tracking rate with forward points: " + order.orderId + ": " + e,
      );
    }

    return spotTrackingRate;
  }
  getRoundedRateString(
    spotTrackingRate: CubiqTS.BigNumber,
    isInvert: boolean,
    pair: CubiqTS.CurrencyPair,
  ): string {
    let minPrecision: number = isInvert ? pair.spotInvertDecimalsQty : pair.pipsQty;
    let maxPrecision: number = isInvert ? pair.fwdInvertDecimalsQty : pair.fwdRateDecimalsQty;
    let roundedRateStr: string = this.pricingService.roundRateToPrecision(
      spotTrackingRate,
      minPrecision,
      maxPrecision,
    );
    return roundedRateStr;
  }

  /* private getLimitRate(order: CubiqTS.OrderRequestData, pair: CubiqTS.CurrencyPair, client:CubiqTS.ClientImpl, isInvert:boolean):CubiqTS.BigNumber{
		if(order.markup){
			let precision:number = Math.max(this.getEntryPrecision(isInvert,pair),this.getMarkupPrecision(isInvert,pair));
			let markupDecs:CubiqTS.BigNumber = this.markupPipsToDecs(order, pair, client, isInvert);
			
			let tracking:CubiqTS.BigNumber = new CubiqTS.BigNumber(this.getLimitPriceFromStrategyParams(order)).plus(markupDecs);
			if(tracking.isLessThan(0)){
				tracking=new CubiqTS.BigNumber(0);   
			}
			return tracking;
		}
		return new CubiqTS.BigNumber(this.getLimitPriceFromStrategyParams(order));
	}

	private getLimitPriceFromStrategyParams(order: CubiqTS.OrderRequestData): string{
		let spLimitPrice = order.strategyParameters.find(item=> item.parameterName === 'limitprice');
		let limitPrice = spLimitPrice != null ? spLimitPrice.parameterValue : '0';
		return typeof limitPrice === 'string' ? limitPrice : '0';
	} */

  private markupPipsToDecs(
    order: CubiqTS.OrderRequestData,
    pair: CubiqTS.CurrencyPair,
    client: CubiqTS.ClientImpl,
    isInvert: boolean,
  ): CubiqTS.BigNumber {
    if (
      order.markup != "" &&
      order.markup != null &&
      order.base &&
      order.nonBase &&
      order.fixedCcy &&
      order.requestOrderType != null &&
      client &&
      ((order.requestOrderType == CubiqTS.RequestOrderType.TP && client.reverseEngineerTPFg) ||
        (this.isStop(order.requestOrderType) && client.reverseEngineerSLFg))
    ) {
      let markup: CubiqTS.BigNumber = new CubiqTS.BigNumber(order.markup.replace(/,/gi, "")).times(
        new CubiqTS.BigNumber(10).pow(-this.getSpotPrecision(isInvert, pair)),
      );
      if (
        (order.clientIntent == CubiqTS.OrderIntent.SELL && order.base == order.fixedCcy) ||
        (order.clientIntent == CubiqTS.OrderIntent.BUY && order.nonBase == order.fixedCcy)
      ) {
        return markup;
      } else {
        return markup.times(-1);
      }
    }
    return new CubiqTS.BigNumber(0);
  }

  private isStop(requestOrderType: CubiqTS.RequestOrderType): boolean {
    switch (requestOrderType) {
      case CubiqTS.RequestOrderType.SL_ASK:
      case CubiqTS.RequestOrderType.SL_ATRATE:
      case CubiqTS.RequestOrderType.SL_BID:
      case CubiqTS.RequestOrderType.TSL:
      case CubiqTS.RequestOrderType.TSL_ASK:
      case CubiqTS.RequestOrderType.TSL_BID:
        return true;
      default:
        return false;
    }
  }

  private getSpotPrecision(isInvert: boolean, pair: CubiqTS.CurrencyPair): number {
    if (pair) {
      if (isInvert) {
        return pair.spotInvertDecimalsQty;
      } else {
        return pair.rateDecimalsQty;
      }
    }
    return 0;
  }

  private getEntryPrecision(isInvert: boolean, pair: CubiqTS.CurrencyPair): number {
    if (pair) {
      if (isInvert) {
        return pair.limitEntrySpotInvertDecimals;
      } else {
        return pair.limitEntrySpotDecimals;
      }
    }
    return 0;
  }

  /* Updated to avoid crashing the app: https://www.bcdfx.com/ijira/browse/OMS-25086 */
  private getMarkupPrecision(isInvert: boolean, pair: CubiqTS.CurrencyPair): number {
    let precision = 0;
    if (pair) {
      if (isInvert) {
        precision = pair.markupSptInvDecimals;
      } else {
        precision = pair.markupSptDecimals;
      }
    }
    return precision != null && typeof precision === "number" && precision >= 0 ? precision : 0;
  }

  /**
   * Gets order trigger logic based on order request. The method is not used any more, since OMS-34469.
   * @param order the order request to be checked
   * @param pair the currency pair model for the currency pair of the order
   */
  protected getTriggerLogic(
    order: CubiqTS.OrderRequestData,
    pair: CubiqTS.CurrencyPair,
  ): CubiqTS.TriggerLogicType {
    if (CubiqTS.RequestorFillTypeRestriction.AUTO == order.fillTypeRestriction) {
      if (this.isTpOrStop(order)) {
        if (CubiqTS.RequestOrderType.TP == order.requestOrderType)
          return pair.triggerAutoTpSpreadType;
        else {
          return pair.triggerAutoSlSpreadType;
        }
      }
    } else if (CubiqTS.RequestorFillTypeRestriction.MANUAL == order.fillTypeRestriction) {
      if (this.isTpOrStop(order)) {
        if (CubiqTS.RequestOrderType.TP == order.requestOrderType)
          return pair.triggerManTpSpreadType;
        else {
          return pair.triggerManSlSpreadType;
        }
      }
    }

    return null;
  }

  /**
   * Side of market & distance from market checks will be changed to be based purely on the economic intent of an order.
   * - Buy Base = Offer.
   * - Sell Base = Bid.
   * - Buy Non-Base = Bid
   * - Sell Non-Base = Offer.
   *
   * @param order the order being checked for tracking side
   */
  protected getTrackingSide(order: CubiqTS.OrderRequestData): CubiqTS.RateSideType {
    let isBuyBase: boolean =
      order.clientIntent == CubiqTS.OrderIntent.BUY && order.fixedCcy == order.base;
    let isSellNonBase: boolean =
      order.clientIntent == CubiqTS.OrderIntent.SELL && order.fixedCcy == order.nonBase;

    return isBuyBase || isSellNonBase ? CubiqTS.RateSideType.OFFER : CubiqTS.RateSideType.BID;
  }

  protected isTpOrStop(order: CubiqTS.OrderRequestData): boolean {
    return (
      CubiqTS.RequestOrderType.TP === order.requestOrderType ||
      CubiqTS.RequestOrderType.SL_ASK === order.requestOrderType ||
      CubiqTS.RequestOrderType.SL_BID === order.requestOrderType ||
      CubiqTS.RequestOrderType.SL_ATRATE === order.requestOrderType ||
      CubiqTS.RequestOrderType.TSL === order.requestOrderType ||
      CubiqTS.RequestOrderType.TSL_ASK === order.requestOrderType ||
      CubiqTS.RequestOrderType.TSL_BID === order.requestOrderType
    );
  }

  protected supportsEntryAmountCheck(order: CubiqTS.OrderRequestData): boolean {
    switch (order.requestOrderType) {
      case CubiqTS.RequestOrderType.CALL:
      case CubiqTS.RequestOrderType.BMALGO:
        return false;
      default:
        break;
    }

    return true;
  }

  protected isOrderAboveMarket(
    order: CubiqTS.OrderRequestData,
    _pair: CubiqTS.CurrencyPair,
  ): boolean {
    if (this.isTpOrStop(order)) {
      if (CubiqTS.RequestOrderType.TP == order.requestOrderType) {
        if (CubiqTS.OrderIntent.SELL == order.clientIntent && order.fixedCcy == order.base) {
          return true;
        } else if (
          CubiqTS.OrderIntent.BUY == order.clientIntent &&
          order.fixedCcy == order.nonBase
        ) {
          return true;
        }
      } else {
        if (CubiqTS.OrderIntent.BUY == order.clientIntent && order.fixedCcy == order.base) {
          return true;
        } else if (
          CubiqTS.OrderIntent.SELL == order.clientIntent &&
          order.fixedCcy == order.nonBase
        ) {
          return true;
        }
      }
    }

    return false;
  }

  protected isOrderBelowMarket(
    order: CubiqTS.OrderRequestData,
    _pair: CubiqTS.CurrencyPair,
  ): boolean {
    if (this.isTpOrStop(order)) {
      if (CubiqTS.RequestOrderType.TP == order.requestOrderType) {
        if (CubiqTS.OrderIntent.BUY == order.clientIntent && order.fixedCcy == order.base) {
          return true;
        } else if (
          CubiqTS.OrderIntent.SELL == order.clientIntent &&
          order.fixedCcy == order.nonBase
        ) {
          return true;
        }
      } else {
        if (CubiqTS.OrderIntent.SELL == order.clientIntent && order.fixedCcy == order.base) {
          return true;
        } else if (
          CubiqTS.OrderIntent.BUY == order.clientIntent &&
          order.fixedCcy == order.nonBase
        ) {
          return true;
        }
      }
    }

    return false;
  }

  public addError(errors: any[], id: string, args: string[]) {
    errors.push(
      JSON.stringify({
        id: id,
        args: args,
      }),
    );
  }

  protected getFormattedRate(rate: string, pair: CubiqTS.CurrencyPair, isInvert: boolean) {
    if (rate == null || rate == "") return "";
    let qty = isInvert ? pair.spotInvertDecimalsQty : pair.pipsQty;

    return this.pricingService.roundRateToPrecision(rate, qty, qty);
  }

  protected getRelevantGammaLegs(
    orderRequest: CubiqTS.GammaOrderRequestImpl,
    wrongSide: boolean,
  ): Array<CubiqTS.OrderRequestDataImpl> {
    let legs: Array<CubiqTS.OrderRequestDataImpl> = [];

    let midIndex: number = orderRequest.midRungIndex;
    if (midIndex >= 0) {
      let legAbove: CubiqTS.OrderRequestDataImpl =
        midIndex > 0 ? orderRequest.getRung(midIndex - 1) : null;
      let legBelow: CubiqTS.OrderRequestDataImpl = orderRequest.getRung(midIndex + 1);

      if (legAbove != null) {
        legs.push(legAbove);
        logger.out.info(
          `${this.constructor.name}: Adding leg above ${legAbove.externalClientReference} to ${
            wrongSide ? "wrong side of market" : "tolerance"
          } check `,
        );
      } else {
        logger.out.info(
          `${this.constructor.name}: No leg above for ${
            wrongSide ? "wrong side of market" : "tolerance"
          } check of strategy ${orderRequest.linkReference}`,
        );
      }

      if (legBelow != null) {
        legs.push(legBelow);
        logger.out.info(
          `${this.constructor.name}: Adding leg below ${legBelow.externalClientReference} to ${
            wrongSide ? "wrong side of market" : "tolerance"
          } check `,
        );
      } else {
        logger.out.info(
          `${this.constructor.name}: No leg below for ${
            wrongSide ? "wrong side of market" : "tolerance"
          } check of strategy ${orderRequest.linkReference}`,
        );
      }
    } else {
      logger.out.error(
        `${this.constructor.name}: Invalid mid rung index on gamma order request - ${midIndex}. Will not be able to perform fat finger checks.`,
      );
    }

    return legs;
  }

  /**
   * Get list of all order legs that are candidates for amount tolerance checking. CALL orders
   * must be removed from this list
   *
   * @param order Root order
   * @param checkSibling Should sibling of root order be checked
   * @param retLegs Return list of order legs to check
   */
  protected getRelevantStrategyLegs(
    order: CubiqTS.OrderRequestData,
    checkSibling: boolean,
    retLegs: Array<CubiqTS.OrderRequestData | CubiqTS.FixingOrderRequestData>,
  ): void {
    if (checkSibling) {
      const siblingOrder = order?.sibling?.order;
      if (siblingOrder instanceof CubiqTS.OrderRequestDataImpl) {
        // Skip root CALL orders
        if (this.supportsEntryAmountCheck(siblingOrder)) {
          retLegs.push(siblingOrder);
        }
        this.getRelevantStrategyLegs(siblingOrder, false, retLegs);
      }
    }

    if (order.childRelationships != null) {
      order.childRelationships
        .filter((r) => r.order != null)
        .map((r) => r.order)
        .forEach((o) => {
          if (this.supportsEntryAmountCheck(o)) {
            retLegs.push(o);
          }
          this.getRelevantStrategyLegs(o, true, retLegs);
        });
    }
  }
}
